import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageTitle, statusColor } from "@/components/DashUI";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUSES = ["APPLIED", "UNDER_REVIEW", "MORE_INFORMATION_REQUIRED", "APPROVED", "REJECTED",
  "AGREEMENT_REQUIRED", "ACTIVE", "SUSPENDED", "INACTIVE"];

export default function AgentApplications() {
  const [apps, setApps] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/agent-applications").then((r) => setApps(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/agent-applications/${id}`, { status }); toast.success(`Status: ${status}`); load(); setSel(null); }
    catch { toast.error("Update failed"); }
  };

  return (
    <div>
      <PageTitle title="Agent Applications" subtitle="Review manually — agents are never auto-approved" />
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="agent-apps-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>{["Name", "Location", "Email", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apps.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50" data-testid={`agent-app-${a.id}`}>
                  <td className="px-4 py-3 font-medium text-navy">{a.name}</td>
                  <td className="px-4 py-3 text-slate-600">{a.city}, {a.state}</td>
                  <td className="px-4 py-3 text-slate-600">{a.email}</td>
                  <td className="px-4 py-3"><Badge className={`${statusColor(a.status)} hover:${statusColor(a.status)}`}>{(a.status || "").replace(/_/g, " ")}</Badge></td>
                  <td className="px-4 py-3"><Button size="sm" variant="outline" onClick={() => setSel(a)} data-testid={`agent-review-${a.id}`}>Review</Button></td>
                </tr>
              ))}
              {apps.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No applications yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{sel?.name}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-3 text-sm">
              <Info label="Email" value={sel.email} />
              <Info label="Phone" value={sel.phone} />
              <Info label="Location" value={`${sel.city}, ${sel.state}`} />
              <Info label="Background" value={sel.professional_background} />
              <Info label="Healthcare exp." value={sel.healthcare_experience} />
              <Info label="Sales exp." value={sel.sales_experience} />
              <Info label="Target markets" value={sel.target_markets} />
              <Info label="Services" value={(sel.service_interests || []).join(", ")} />
              <Info label="Reason" value={sel.reason_for_joining} />
              <Info label="W-9" value={sel.w9_status} />
              <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                Acknowledgments: compliance {sel.agree_compliance ? "✓" : "✗"}, income-not-guaranteed {sel.ack_income_not_guaranteed ? "✓" : "✗"}, not-employee {sel.ack_not_employee ? "✓" : "✗"}, written-comp {sel.ack_compensation_written ? "✓" : "✗"}
              </div>
              <div className="pt-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Set status</label>
                <Select onValueChange={(v) => setStatus(sel.id, v)}>
                  <SelectTrigger className="mt-1" data-testid="agent-status-select"><SelectValue placeholder={sel.status} /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button className="flex-1 bg-emeraldbrand hover:bg-emeraldbrand/90" data-testid="agent-approve-btn" onClick={() => setStatus(sel.id, "APPROVED")}>Approve</Button>
                <Button variant="outline" className="flex-1" data-testid="agent-reject-btn" onClick={() => setStatus(sel.id, "REJECTED")}>Reject</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Info({ label, value }) {
  return <div><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}: </span><span className="text-slate-700">{value || "—"}</span></div>;
}
